import * as angular from 'angular';

(function () {
    'use strict';

    /**
     * @ngdoc directive
     * @name pkpPriceUnit
     * @memberof app
     * @restrict E
     *
     * @description
     * The backend provides a list of units which are valid to be submitted as the price unit for a i.e. a bid.
     * This directive will empower the user to select his favored unit from the possible units in a nice way.
     * It's applied to an input field like a bootstrap input add-on and it also looks that way.
     * The directive optionally takes advantage of the {@link pkpOrderCurve} directive. Whether it can be found or not
     * is also the flag as to whether or not this price is part of an order curve or is a bid.
     *
     * @param {Object|Object[]} priceDtoRef The object (preferably the model) the selected unit should be bound to.
     *  If it's an array, it will also work as `possibleUnitsRef`. This mostly signals an edit mode,
     *  where the possible units are pre-set through the `priceDtoRef`.
     * @param {Object[]} possibleUnitsRef A list of valid `PriceUnitDto`s that the user can choose from.
     *  The choice of an element here will be assigned to the `priceDtoRef`. If `priceDtoRef` is an array,
     *  it will be assigned a copy of the `priceDtoRef`. Read `priceDtoRef` param description for more.
     * @param {boolean} isFirst Indicates if it's the first occurrence in the curve. Only for the first occurrence
     *  the unit can be selected and changed for the whole curve. Defaults to true.
     *
     * @attr {PkpUseOriginals} pkpUseOriginals
     *
     * @example
     <div class="input-group">
         <input type="number" class="form-control" id="price" ng-model="bid.price.value" name="price"
         required>
         <pkp-price-unit possible-units="auction.possibleUnits" price-dto="bid.price">
         </pkp-price-unit>
     </div>
     *
     * @author flzemke.btc
     * @ngInject
     */
    angular.module('app').directive('pkpPriceUnit', pkpPriceUnit);

    function pkpPriceUnit($filter, fieldKeyService) {
        return {
            restrict: 'E',
            scope: {
                priceDtoRef: '=priceDto',
                possibleUnitsRef: '=possibleUnits',
                isFirst: '=?'
            },
            require: '?^^pkpOrderCurve',
            replace: true,
            template: require('app/_directives/pkp-price-unit.directive.html'),
            link: linkFn
        };

        function linkFn(scope, elem, attr, pkpOrderCurve) {
            var fieldKey = fieldKeyService.initialize(attr.pkpUseOriginals);

            /**
             * The reference will be assigned to a new isolated scope in order to prevent
             * two-way binding to trigger.
             * So this value is in this directive's isolated scope and the reference is
             * the parameter passed to the directive.
             * Work can be done with the isolated scope and when changes should be applied,
             * the reference can be changed.
             *
             * @type {Object}
             */
            scope.priceDto = scope.priceDtoRef;
            /**
             * {@inheritDoc scope.priceDto}
             */
            scope.possibleUnits = scope.possibleUnitsRef;
            scope.jsonUnitKey = fieldKey.unitKey;
            scope.jsonValueKey = fieldKey.valueKey;
            scope.setPriceDto = setPriceDto;
            scope.selectorIsActive = selectorIsActive;

            (function init() {
                initializePossibleUnits();
                initializePriceDto();
            })();

            function setPriceDto(possibleUnit) {
                if (pkpOrderCurve) {
                    pkpOrderCurve.onUnitChange();
                }
                scope.priceDto = possibleUnit;
                scope.priceDtoRef = scope.priceDto;
            }

            /**
             * The unit can be selected when there's more than one element in {@link scope.possibleUnits} and
             * when it's an order curve ({@link pkpOrderCurve}), the selection can only be done on the first
             * input field as it's representing the currency for all the other prices.
             *
             * @returns {boolean} <tt>true</tt>, if the unit can be selected, <tt>false</tt> otherwise.
             */
            function selectorIsActive() {
                if (pkpOrderCurve) {
                    return scope.possibleUnits.length > 1 && scope.isFirst;
                }
                return scope.possibleUnits.length > 1;
            }

            /**
             * This is for the case, when the <tt>priceDto</tt> is not an array, in this case a value node needs to be
             * added to the <tt>possibleUnits</tt> array that is then served from the <tt>priceDto</tt>.
             *
             * @example
             * [{}] // First way with object being the unit directly
             * [unit: {}, value: {}}] // Second way with another level of objects
             */
            function consolidatePossibleUnits(possibleUnits, priceDto) {
                if (!priceDto) {
                    priceDto = {};
                }

                if (possibleUnits[0].hasOwnProperty(fieldKey.unitKey)) {
                    return possibleUnits;
                }

                var newPossibleUnitsWithRightStructure = [];

                for (var i = 0; i < possibleUnits.length; i++) {
                    var obj = possibleUnits[i];
                    var newPossibleUnitWithRightStructure = {};
                    newPossibleUnitWithRightStructure[fieldKey.unitKey] = obj;
                    newPossibleUnitWithRightStructure[fieldKey.valueKey] =
                        priceDto[fieldKey.valueKey];
                    newPossibleUnitsWithRightStructure.push(
                        newPossibleUnitWithRightStructure
                    );
                }

                return newPossibleUnitsWithRightStructure;
            }

            /**
             * Depending on whether the price is part of a order curve or a single bid different actions
             * have to be taken and this will two-way bind the initial <tt>priceDto</tt>.
             */
            function initializePriceDto() {
                if (pkpOrderCurve) {
                    if (scope.isFirst === true) {
                        twoWayBindPriceDto(scope.possibleUnits[0]);
                    } else {
                        if (angular.isUndefined(scope.priceDto)) {
                            scope.priceDto = {};
                            scope.priceDto[fieldKey.valueKey] = {};
                            scope.priceDto[fieldKey.unitKey] = {};
                        }

                        var newPriceDtoToTwoWayBind = {};
                        newPriceDtoToTwoWayBind[fieldKey.unitKey] =
                            pkpOrderCurve.getUnitOfFirstPoint(fieldKey.unitKey);
                        newPriceDtoToTwoWayBind[fieldKey.valueKey] =
                            scope.priceDto[fieldKey.valueKey] ||
                            scope.priceDto[0][fieldKey.valueKey];
                        twoWayBindPriceDto(newPriceDtoToTwoWayBind);
                    }
                } else {
                    twoWayBindPriceDto(scope.possibleUnits[0]);
                }
            }

            /**
             * The {@link pkpPriceUnit} doc will describe why this is necessary.
             */
            function initializePossibleUnits() {
                if (angular.isArray(scope.priceDto)) {
                    scope.possibleUnits = $filter('unique')(scope.priceDto);
                } else {
                    scope.possibleUnits = consolidatePossibleUnits(
                        scope.possibleUnits,
                        scope.priceDto
                    );
                }
            }

            /**
             * Update {@link scope.priceDto} as well as {@link scope.priceDtoRef}.
             *
             * @param {Object} newPriceObj
             * @param {string} newPriceObj.value
             * @param {Object} newPriceObj.unit
             */
            function twoWayBindPriceDto(newPriceObj) {
                scope.priceDtoRef = newPriceObj;
                scope.priceDto = newPriceObj;
            }
        }
    }
})();
